import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faTrash, faPlus } from "@fortawesome/free-solid-svg-icons"
import { round20 } from "../../../utilities/Addons"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles } from "../../styles/styles"
import { tableStyles } from "../../styles/tableStyles"
import CustomModal, {
	DeletingCustomModal,
} from "../../basics/interface/CustomModal"
import {
	deleteAtelierModele,
	deleteAtelierModeleReset,
	fetchAtelierModeles,
} from "../../../store/actions/atelierModeleActions"

// Liste V2 des modèles atelier (gabarits). Onglets Client / SVP (filtre `svp`),
// double-clic ou clic → fiche, suppression confirmée par CustomModal.
const AtelierModeleListV2 = () => {
	const dispatch = useDispatch()
	const history = useHistory()
	const { atelierModeleInfo } = useSelector((state) => state)
	const { listeAtelierModeles, fetchAtelierModeles: fetchStatus } =
		atelierModeleInfo
	const deleteStatus = atelierModeleInfo.deleteAtelierModele
	const masterLabel = useSelector(
		(state) => state.masterReducer.masterEntite.label || "SVP",
	)

	const [onglet, setOnglet] = useState("client") // "client" | "svp"
	const [aSupprimer, setASupprimer] = useState(null) // { id, nom } | null
	const [hover, setHover] = useState(null)

	useEffect(() => {
		dispatch(fetchAtelierModeles())
	}, [dispatch])

	const modeles = useMemo(() => {
		const isSvp = onglet === "svp"
		return listeAtelierModeles.filter((am) => am.svp === isSvp)
	}, [onglet, listeAtelierModeles])

	// Ferme le modal de suppression une fois l'opération terminée.
	useEffect(() => {
		if (aSupprimer && deleteStatus.deleted) {
			setASupprimer(null)
			dispatch(deleteAtelierModeleReset())
		}
	}, [deleteStatus.deleted, aSupprimer, dispatch])

	const confirmerSuppression = () =>
		dispatch(deleteAtelierModele(JSON.stringify({ id: aSupprimer.id })))

	return (
		<div style={generalStyles.page}>
			<div style={{ maxWidth: 760, margin: "0 auto" }}>
			{/* En-tête */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginBottom: 20,
				}}
			>
				<h2
					style={{
						fontSize: 22,
						fontWeight: 700,
						color: COLORS.text,
						margin: 0,
					}}
				>
					Modèles atelier
				</h2>
				<button
					type="button"
					style={generalStyles.btnPrimary}
					onClick={() => history.push("/atelier/modeles/0")}
				>
					<FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
					Nouveau
				</button>
			</div>

			{/* Sélecteur principal Client / master (segmented control, comme /configuration) */}
			<div style={generalStyles.segmented}>
				{[
					["client", "Client"],
					["svp", masterLabel],
				].map(([key, label]) => (
					<button
						key={key}
						type="button"
						onClick={() => setOnglet(key)}
						style={generalStyles.segment(onglet === key)}
					>
						{label}
					</button>
				))}
			</div>

			{fetchStatus.loading ? (
				<div style={{ textAlign: "center", padding: 32 }}>
					<FontAwesomeIcon
						icon={faSync}
						spin
						style={{ color: COLORS.textLight }}
					/>
				</div>
			) : (
				<div style={tableStyles.container}>
					<table style={tableStyles.table}>
						<thead>
							<tr>
								<th style={tableStyles.th}>Nom</th>
								<th style={tableStyles.thRight}>Tarif TTC</th>
								<th
									style={{
										...tableStyles.thRight,
										width: 60,
									}}
								/>
							</tr>
						</thead>
						<tbody>
							{modeles.length === 0 ? (
								<tr>
									<td colSpan={3} style={tableStyles.empty}>
										Aucun modèle.
									</td>
								</tr>
							) : (
								modeles.map((am, i) => (
									<tr
										key={`am_${am.id}`}
										onDoubleClick={() =>
											history.push(
												`/atelier/modeles/${am.id}`,
											)
										}
										onMouseEnter={() => setHover(am.id)}
										onMouseLeave={() => setHover(null)}
										style={{
											cursor: "pointer",
											backgroundColor:
												hover === am.id
													? COLORS.accentLight
													: tableStyles.rowBg(i),
										}}
									>
										<td
											style={{
												...tableStyles.td,
												fontWeight: 500,
												textAlign: "left",
											}}
										>
											{am.nom}
										</td>
										<td style={tableStyles.tdRight}>
											{round20(am.total).toFixed(2)}
										</td>
										<td style={tableStyles.tdRight}>
											<button
												type="button"
												title="Supprimer"
												onClick={(e) => {
													e.stopPropagation()
													setASupprimer({
														id: am.id,
														nom: am.nom,
													})
												}}
												style={generalStyles.iconBtn(
													COLORS.danger,
												)}
											>
												<FontAwesomeIcon
													icon={faTrash}
												/>
											</button>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			)}

			{/* Confirmation de suppression */}
			{aSupprimer &&
				(deleteStatus.deleting || deleteStatus.error ? (
					<DeletingCustomModal
						status={deleteStatus}
						onClose={() => {
							setASupprimer(null)
							dispatch(deleteAtelierModeleReset())
						}}
					/>
				) : (
					<CustomModal
						title="Supprimer le modèle ?"
						subtitle={aSupprimer.nom}
						onClose={() => setASupprimer(null)}
						showCloseButton={false}
						buttons={[
							{
								title: "Annuler",
								styleName: "secondary",
								onClick: () => setASupprimer(null),
							},
							{
								title: "Supprimer",
								styleName: "danger",
								onClick: confirmerSuppression,
							},
						]}
					>
						<div style={{ color: COLORS.textMuted, fontSize: 14 }}>
							Cette action est définitive.
						</div>
					</CustomModal>
				))}
			</div>
		</div>
	)
}

export default AtelierModeleListV2
